import React, { Fragment, useState } from "react";
import { connect } from "react-redux";

import {
  CPage,
  CRow,
  H1,
  CPanel,
  CDropDown,
  Text
} from "@appcues/component-library";
import PropTypes from "prop-types";

import ChildPage from "components/Common/ChildPage";
import PublishStatusTag from "components/Common/PublishStatusTag";
import Loader from "components/Common/Loader";
import { selectFlow, selectAreFlowsSynced } from "reducers/account/flows";
import EmptyState from "./EmptyState";
import FlowAnalyticsActions from "./FlowAnalyticsActions";
import FlowActivityChart from "./FlowActivityChart";
import PreviewCarousel from "./PreviewCarousel";
import StepByStepTable from "./StepByStepTable";
import RecentUsersTable from "./RecentUsersTable";
import IssuesTable from "./IssuesTable";
import Scoreboard from "./Scoreboard";
import { START_TIMES } from "./constants";

export const FlowAnalytics = ({ flow, loaded }) => {
  const [startTime, setStartTime] = useState("7 days ago");

  const startTimeOptions = Object.entries(START_TIMES).map(
    ([value, label]) => ({
      label,
      value
    })
  );

  if (!loaded) {
    return <Loader />;
  }

  return (
    <ChildPage
      createdAt={flow.createdAt}
      createdByUserId={flow.createdBy}
      updatedAt={flow.updatedAt}
      updatedByUserId={flow.updatedBy}
      breadcrumbContents={{
        label: "Flows",
        path: "/flows",
        child: {
          label: `${flow.name}`
        }
      }}
      components={{
        Title: function Header() {
          return (
            <Fragment>
              <H1 title={flow.name}>{flow.name}</H1>
              <PublishStatusTag published={flow.published} />
            </Fragment>
          );
        }
      }}
      subHeaderActions={
        <FlowAnalyticsActions
          flowId={flow.id}
          isLocalized={flow.lockedForLocalization}
          createdAt={flow.createdAt}
        />
      }
    >
      <CPage.Container>
        {flow.publishedAt && (
          <CRow>
            <CDropDown
              className="start-time-dropdown"
              options={startTimeOptions}
              value={startTime}
              onChange={setStartTime}
            />
          </CRow>
        )}
        <CRow marginTop="30px">
          <CPanel>
            <Text type="tertiary" marginBottom="10px" alignSelf="end">
              {flow.publishedAt
                ? "Displaying most recently published version"
                : "Displaying most recent draft"}
            </Text>
            <PreviewCarousel flowId={flow.id} />
          </CPanel>
        </CRow>
        {flow.publishedAt ? (
          <Fragment>
            <CRow marginTop="30px">
              <CPanel title="Flow analytics">
                <p>Key metrics about your flow performance</p>
                <FlowActivityChart flowId={flow.id} startTime={startTime} />
              </CPanel>
            </CRow>
            <CRow marginTop="30px">
              <Scoreboard flowId={flow.id} startTime={startTime} />
            </CRow>
            <CRow marginTop="30px">
              <StepByStepTable flowId={flow.id} startTime={startTime} />
            </CRow>
            <CRow marginTop="30px">
              <IssuesTable flowId={flow.id} startTime={startTime} />
            </CRow>
            <CRow marginTop="30px">
              <RecentUsersTable flowId={flow.id} startTime={startTime} />
            </CRow>
          </Fragment>
        ) : (
          <CRow marginTop="30px">
            <EmptyState />
          </CRow>
        )}
      </CPage.Container>
    </ChildPage>
  );
};

FlowAnalytics.propTypes = {
  flow: PropTypes.object.isRequired,
  loaded: PropTypes.bool.isRequired
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { stepId: flowId }
    }
  }
) => ({
  flow: selectFlow(state, flowId),
  loaded: selectAreFlowsSynced(state)
});

export default connect(mapStateToProps)(FlowAnalytics);
